<template>
  <Marker :options="markerOptions" @click="onClick" @mouseover="onMouseOver" @mouseout="onMouseOut">
    <InfoWindow ref="infoWindowRef" :options="infoWindowOptions" v-if="renderInfoWindow"
                v-model="isInfoWindowOpenInternal"
                @closeclick="onInfoWindowClosed">
      <QuickMapInfo v-if="isInfoWindowOpenInternal" v-bind="mapInfoData"></QuickMapInfo>
    </InfoWindow>
  </Marker>
</template>

<script>
import {Marker, InfoWindow} from "vue3-google-map";
import {dataManipulator, entityDataManager, WorkflowManager} from "@/assets/js/data-manipulation";
import QuickMapInfo from "@/components/pages/dashboard/QuickMapInfo.vue";
import {EventSeverity} from "@/assets/js/events-info";
import {mapGetters, mapState} from "vuex";
import moment from "moment";

export default {
  name: 'VehicleMarker',
  components: {QuickMapInfo, Marker, InfoWindow},
  props: {
    positionData: Object,
    openInfoOnClick: {
      type: Boolean,
      default: true
    },
    openInfoOnHover: {
      type: Boolean,
      default: false
    },
    basicMode: Boolean,
    includeAddressOnly: Boolean,
    includeGpsParams: {
      type: Boolean,
      default: true
    },
    includeTripsButton: {
      type: Boolean,
      default: false
    },
    includeLoadTripsButton: {
      type: Boolean,
      default: false
    },
    autoCenter: {
      type: Boolean,
      default: true
    },
    customInfoWindowOptions: Object,
  },
  emits: ['click', 'infoClosed', 'loadTrips'],
  data() {
    return {
      renderInfoWindow: false,
      isInfoWindowOpenInternal: true,
      markerVisible: true,
      infoWindowShownOnHover: false,
    };
  },
  computed: {
    ...mapState(['vehicles', 'devices', 'events', 'drivers', 'deviceDrivers', 'deviceIgnitions']),
    ...mapGetters(['dashboardQuickMapInfoFields']),
    markerOptions() {
      let ignition = entityDataManager.getPositionAttribute(this.positionData, 'ignition', false);
      return {
        position: this.position,
        visible: this.markerVisible,
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 5,
          fillColor: ignition ? 'green' : 'red',
          fillOpacity: 0.8,
          strokeWeight: 1,
          rotation: this.positionData.course || 0,
        },
        label: {
          className: 'map-label',
          text: this.vehicle?.shortName ?? '',
        }
      }
    },
    infoWindowOptions() {
      return {
        disableAutoPan: !this.autoCenter,
        ...this.customInfoWindowOptions
      };
    },
    mapInfoData() {
      // todo move to QuickMapInfo and get rid of timeout for alarm function
      const data = {
        showTripsButton: this.includeTripsButton,
        showLoadTripsButton: this.includeLoadTripsButton,
        onShowTrips: () => this.onShowTrips(),
        onLoadTrips: () => this.onLoadTrips(),
        deviceId: this.deviceId,
        carTitle: this.vehicle.fullName || this.$t('dashboard.quick-map.info-window.no-car-title'),
        deviceTitle: this.device.name || this.$t('dashboard.quick-map.info-window.no-car-title'),
        lastUpdateTime: entityDataManager.getTimeByPosition(this.positionData),
        ignition: entityDataManager.getDeviceIgnition(this.deviceId, this.deviceIgnitions),
        speedStatus: entityDataManager.getSpeedStatusByPosition(this.positionData),
        visibleFields: this.dashboardQuickMapInfoFields,
      };

      if (!this.basicMode) {

        if (this.includeAddressOnly) {
          data['address'] = entityDataManager.getAddressByPosition(this.positionData);
        }
        else {
          const beaconUUID = this.deviceDrivers.get(this.deviceId)?.beaconUUID;

          let engineHours = null;
          if (this.vehicle?.engineHoursSupported) {
            const engineTime = entityDataManager.getEngineTimeByPosition(this.positionData);
            const hours = dataManipulator.millisecondsToTime(engineTime);
            engineHours = this.$t('reports.trips.result.engine-hours', hours);
          }

          data['address'] = entityDataManager.getAddressByPosition(this.positionData);
          data['fuel'] = entityDataManager.getFuelByPosition(this.positionData, this.vehicle?.tankCapacity);
          data['fuelCapStatus'] = entityDataManager.getFuelCapStatusByPosition(this.positionData);
          data['engineHours'] = engineHours;
          if (this.includeGpsParams) {
            data['gpsParams'] = entityDataManager.getGpsParamsByPosition(this.positionData);
          }
          if (beaconUUID != null){
            data['driver'] = entityDataManager.getDriverByBeaconUUID(beaconUUID, this.drivers, '-');
          }
          else{
            data['driver'] = this.vehicle?.driverName;
          }

          data['alarm'] = () => {
            const event = entityDataManager.getLastNonSeenEventByDeviceIdAndSeverity(this.events, this.deviceId, EventSeverity.warning);
            if (!event) {
              return null;
            }

            const geoFenceName = entityDataManager.getGeoFenceName(event.geofenceId, this.geofences);
            return entityDataManager.getTranslatedMessageByEvent(event.type, event.attributes?.alarm, geoFenceName, event.maintenanceName);
          }
        }
      }

      return data;
    },
    vehicle() {
      return entityDataManager.getVehicleByDeviceId(this.vehicles, this.deviceId)
    },
    device() {
      return entityDataManager.getById(this.devices, this.deviceId);
    },
    deviceId() {
      return this.positionData.deviceId;
    },
    position() {
      return entityDataManager.getGoogleGeoByPosition(this.positionData);
    }
  },
  methods: {
    onClick() {
      this.$emit('click', this);
      if (this.openInfoOnClick) {
        this.showInfoWindow();
      }
    },
    onShowTrips() {
      if (!this.position) {
        return;
      }

      const fromDate = moment().startOf('day').toDate();
      const toDate = moment().endOf('day').toDate();

      WorkflowManager.openTripsMaps(this.deviceId, fromDate, toDate, this.positionData.fixTime);
    },
    onLoadTrips() {
      this.$emit('loadTrips', this.deviceId);
    },
    onInfoWindowClosed() {
      this.$emit('infoClosed', this);
    },
    showInfoWindow() {
      this.infoWindowShownOnHover = false;
      this.renderInfoWindow = true;
      setTimeout(() => this.$refs.infoWindowRef?.open(), 10);
      this.isInfoWindowOpenInternal = true;
    },
    closeInfoWindow() {
      this.$refs.infoWindowRef?.close();
      this.renderInfoWindow = false;
      this.infoWindowShownOnHover = false;
      this.isInfoWindowOpenInternal = true;
    },
    isInfoWindowOpen() {
      return this.$refs.infoWindowRef?.infoWindow.isOpen;
    },
    setVisible(visible) {
      this.markerVisible = visible;
    },
    onMouseOver() {
      if (!this.openInfoOnHover) {
        return;
      }

      this.showInfoWindow();
      this.infoWindowShownOnHover = true;
    },
    onMouseOut() {
      if (!this.openInfoOnHover ||!this.infoWindowShownOnHover) {
        return;
      }

      this.closeInfoWindow();
    }
  }
}
</script>

<style scoped lang="scss">

</style>
